/**
 * ContractHistorySection Component
 *
 * Modern table design for contract history
 * Production-grade with responsive design and hover effects
 */

import React from "react";
import { HiDocumentText } from "react-icons/hi";
import CollapsibleSection from "../components/CollapsibleSection";

const ContractHistorySection = ({ data }) => {
  const getDaysBetweenDates = (date1, date2) => {
    const firstDate = date1 ? new Date(date1) : new Date();
    const secondDate = date2 ? new Date(date2) : new Date();
    const differenceInMilliseconds = secondDate - firstDate;
    const differenceInDays = Math.floor(differenceInMilliseconds / (1000 * 60 * 60 * 24));
    return differenceInDays;
  };

  const formatDisplayDate = (isoDate) => {
    if (!isoDate) return "-----";
    return new Date(isoDate)
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .split(" ")
      .join("-")
      .toUpperCase();
  };

  const rawContractHistory = data?.contractHistory || [];

  // Debug: Log contract history to check for duplicates
  console.log("Contract History Data (Raw):", rawContractHistory);

  // Remove duplicates based on journey_id (unique identifier)
  const contractHistory = rawContractHistory.filter((contract, index, self) =>
    index === self.findIndex((c) => c.journey_id === contract.journey_id)
  );

  console.log("Contract History Data (Deduplicated):", contractHistory);

  const columns = [
    { key: "sequence", label: "SEQUENCE", width: "w-20" },
    { key: "projectName", label: "PROJECT NAME", width: "w-64" },
    { key: "startDate", label: "START DATE", width: "w-32" },
    { key: "endDate", label: "END DATE", width: "w-32" },
    { key: "tenure", label: "TENURE (DAYS)", width: "w-32" },
  ];

  return (
    <CollapsibleSection
      title="CONTRACT HISTORY"
      icon={<HiDocumentText className="w-5 h-5" />}
      badge={contractHistory.length > 0 ? `${contractHistory.length} Contracts` : null}
      defaultCollapsed={true}
    >
      {contractHistory.length === 0 ? (
        <div className="text-center text-gray-500 py-8">
          No contract history available
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            {/* Table Header */}
            <thead className="bg-gradient-to-r from-gray-100 to-gray-50">
              <tr>
                {columns.map((column, index) => (
                  <th
                    key={index}
                    scope="col"
                    className={`px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider ${column.width}`}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>

            {/* Table Body */}
            <tbody className="bg-white divide-y divide-gray-100">
              {contractHistory.map((contract, index) => (
                <tr
                  key={contract.journey_id || index}
                  className="hover:bg-blue-50 transition-colors duration-150"
                >
                  {/* Sequence */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-800 font-semibold text-sm">
                      {index + 1}
                    </span>
                  </td>

                  {/* Project Name */}
                  <td className="px-6 py-4">
                    <div className="text-sm font-semibold text-gray-900">
                      {contract.Project_Name || "-"}
                    </div>
                  </td>

                  {/* Start Date */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-700 font-medium">
                      {formatDisplayDate(contract.employee_contract_start__date)}
                    </div>
                  </td>

                  {/* End Date */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-700 font-medium">
                      {formatDisplayDate(contract.employee_contract_end__date)}
                    </div>
                  </td>

                  {/* Tenure */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-800">

                      {contract.employee_contract_end__date && contract.employee_contract_start__date ? `${getDaysBetweenDates(
                        contract.employee_contract_start__date,
                        contract.employee_contract_end__date
                      )} days` : '-'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>

            {/* Table Footer with Total */}
            <tfoot className="bg-gradient-to-r from-gray-50 to-gray-100">
              <tr>
                <td colSpan="4" className="px-6 py-4 text-right text-sm font-bold text-gray-700 uppercase">
                  Total Tenure:
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-blue-100 text-blue-800">
                    {
                      contractHistory.reduce(
                        (total, contract) =>
                          total +
                          (contract.employee_contract_start__date &&
                            contract.employee_contract_end__date
                            ? getDaysBetweenDates(
                              contract.employee_contract_start__date,
                              contract.employee_contract_end__date
                            )
                            : 0),
                        0
                      )
                    } days
                  </span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </CollapsibleSection>
  );
};

export default ContractHistorySection;
